/**
 * Tenant Configuration Import/Export
 * Task 33: Phase 3 - Frontend Integration
 * 
 * Import and export tenant configurations as JSON files.
 */

import React, { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Download, 
  Upload, 
  FileJson, 
  AlertCircle,
  Check,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react';

import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { Modal } from '../UI/Modal';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { 
  exportTenantConfig, 
  importTenantConfig,
  validateTenantConfig,
} from '../../api/tenant-config';
import type { TenantConfigUpdateRequest } from '../../types/tenant-config';

interface TenantConfigImportExportProps {
  tenantId: string;
  tenantName: string;
  onImported?: () => void;
}

export function TenantConfigImportExport({ 
  tenantId, 
  tenantName, 
  onImported 
}: TenantConfigImportExportProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<string>('');
  const [importData, setImportData] = useState<string>('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: () => exportTenantConfig(tenantId),
    onSuccess: (data) => {
      // Format JSON for display/download
      const jsonString = JSON.stringify(data, null, 2);
      setPreviewData(jsonString);
      setShowPreview(true);
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (config: TenantConfigUpdateRequest) => {
      // First validate
      const validation = await validateTenantConfig(tenantId, false);
      if (!validation.isValid) {
        throw new Error(validation.errors?.map(e => e.message).join(', ') || 'Validation failed');
      }
      
      // Then import
      return importTenantConfig(tenantId, config, true);
    },
    onSuccess: () => {
      setShowImportModal(false);
      setImportData('');
      setImportError(null);
      onImported?.();
    },
    onError: (error: Error) => {
      setImportError(error.message);
    },
  });

  const handleDownload = () => {
    if (!previewData) return;
    
    const blob = new Blob([previewData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenant-config-${tenantName.toLowerCase().replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = () => {
    if (previewData) {
      navigator.clipboard.writeText(previewData);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
      setShowImportModal(true);
      setImportError(null);
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importData);
      
      // Extract config from export format
      const config = parsed.config || parsed;
      
      importMutation.mutate(config);
    } catch (error) {
      setImportError('Invalid JSON format');
    }
  };

  const maskSecrets = (json: string): string => {
    // Mask password, api_key, connection_string, etc.
    return json.replace(
      /"(password|api_key|apiKey|connection_string|connectionString|primary_key|primaryKey|secret)":\s*"[^"]+"/gi,
      '"$1": "••••••••"'
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-900">Import/Export Configuration</h3>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-1" />
            Import
          </Button>
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Download className="w-4 h-4 mr-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Important Notes:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Exported configs do NOT include passwords or API keys</li>
              <li>You must re-enter secrets after importing</li>
              <li>Importing will overwrite existing configuration</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Export Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Export Configuration"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-600">
                Configuration for {tenantName}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSecrets(!showSecrets)}
              >
                {showSecrets ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyToClipboard}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-auto max-h-96">
            {showSecrets ? previewData : maskSecrets(previewData)}
          </pre>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-1" />
              Download JSON
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Configuration"
        size="lg"
      >
        <div className="space-y-4">
          {importError && (
            <Card className="p-3 bg-red-50 border-red-200">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{importError}</span>
              </div>
            </Card>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Configuration JSON
            </label>
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              rows={15}
              className="w-full px-3 py-2 font-mono text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Paste your configuration JSON here..."
            />
          </div>

          <Card className="p-3 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">
                This will overwrite the existing configuration for {tenantName}
              </span>
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleImport}
              disabled={!importData || importMutation.isPending}
            >
              {importMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Import
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}





